'use client';

import type { Initializer } from '@scale/scaleui-radix/dist/utils/context-store';
import { createContextStore } from '@scale/scaleui-radix/dist/utils/context-store';
import { persist, createJSONStorage } from 'zustand/middleware';
import { allThemesRoutes } from '../utils/themesRoutes';

type NotificationsState = {
  visitedRoutes: string[];
};

type NotificationsActions = {
  onVisitedRoutesChange: (visitedRoutes: string[]) => void;
  onAddVisitedRoute: (route: string) => void;
};

type NotificationsStore = NotificationsState & NotificationsActions;

const defaultRoutes = allThemesRoutes.map((route) => route.slug);

export const getNotificationsInitializer = (): Initializer<NotificationsStore> =>
  persist<NotificationsStore>(
    (set, get) =>
      ({
        visitedRoutes: defaultRoutes,
        onVisitedRoutesChange: (visitedRoutes: string[]) => {
          set({ visitedRoutes });
        },
        onAddVisitedRoute: (route: string) => {
          if (!get().visitedRoutes.includes(route)) {
            set({
              visitedRoutes: [...get().visitedRoutes, route],
            });
          }
        },
      } as NotificationsStore),
    {
      name: 'notifications-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (!state?.visitedRoutes.length) {
          return { visitedRoutes: defaultRoutes };
        }
        return state;
      },
    }
  ) as unknown as Initializer<NotificationsStore>;

export const {
  Provider: NotificationsProvider,
  SyncPropWithStore: SyncPropWithNotificationsStore,
  useStore: useNotifications,
  useStoreApi: useNotificationsApi,
} = createContextStore<NotificationsStore>();
